import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction, i18n } from "i18next";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import { FaPlus, FaRegFile, FaTrashAlt } from "react-icons/fa";
import U2000ResponsiveToolbar from "../../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";
import { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { u2000IntFormState, useU2000FormStateHandlers, type U2000FormSBusinessHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import u2000FormState from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000IdColumnDef, type U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import { U2000IdCheckedBoxDisplay } from "../../../../../u2000Core/components/tables/display/U2000IdCheckedBoxDisplay";
import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import type { U3121ProjectContact, U3121ProjectContactWithSource } from "../../../../U3121Service";
import { U3120ContactPhoneType, emptyProjectContact } from "../../../../U3121Service";
import { NS, U3121ROLES } from "../../../../U3121";
import { useU3121FindContactTypesHandlers } from "../../../../U3121UseFindHandlers";
import { u3121ProjectContactHandler, type U3121ProjectFormExtendedHandlers, type U3121ProjectContactHandlerParam } from "./U3121ProjectContactHandlers";
import { u3121ProjectContactValidationErrors } from "./u3121ProjectContactUtils";
import U3121ProjectContactEditDlg from "./U3121ProjectContactEditDlg";
import U3121ProjectContactDeleteDlg from "./U3121ProjectContactDeleteDlg";

function getColumnsDef(t: TFunction, i18n: i18n): U2000TableColumn[] {
  return [
    {
      ...U2000IdColumnDef,
      display: (column: U2000TableColumn, value: unknown, row: U3121ProjectContactWithSource, allowRowSelection: boolean, rowUpdated?: (r: unknown) => void) => {
        const isDisabled = row?.source === "customer";
        const content = (
          <U2000IdCheckedBoxDisplay
            column={column}
            value={value}
            row={(row ?? {}) as unknown as Record<string, unknown>}
            disabled={isDisabled}
            rowUpdated={rowUpdated}
            allowRowSelection={allowRowSelection}
          />
        );
        return isDisabled ? (
          <span title={t("msgContactRecordNotModifiable", { ns: NS })} className="inline-flex items-center justify-center w-full">
            {content}
          </span>
        ) : (
          content
        );
      },
    },
    {
      name: "source",
      label: t("lblSource", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[12%]",
      type: "string",
      align: "left",
      display: (_column: U2000TableColumn, _value: unknown, row: U3121ProjectContactWithSource) => {
        if (!row) return "";
        const lang = i18n?.language || "fr";
        if (row.sourceLabelFr != null && row.sourceLabelEn != null) return lang === "en" ? row.sourceLabelEn : row.sourceLabelFr;
        if (row.source === "customer") return t("contactSourceCustomer", { ns: NS });
        if (row.source === "project") return t("contactSourceProject", { ns: NS });
        return "";
      },
    },
    {
      name: "contactName",
      label: t("lblContactName", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: "w-[30%]",
      type: "string",
      align: "left",
    },
    {
      name: "contactType",
      label: t("lblContactType", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[15%]",
      type: "string",
      align: "left",
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- display(column, value, row) : row non utilisé
      display: (_column: U2000TableColumn, value: unknown, _row: U3121ProjectContact) => {
        if (!value || typeof value !== "object") return "";
        const v = value as Record<string, unknown>;
        const lang = i18n?.language || "fr";
        if (lang === "fr" && v.contactTypeNameFr) return String(v.contactTypeNameFr);
        if (lang === "en" && v.contactTypeNameEn) return String(v.contactTypeNameEn);
        return String(v.contactTypeCode ?? "");
      },
    },
    {
      name: "email",
      label: t("lblEmail", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[20%]",
      type: "string",
      align: "left",
    },
    {
      name: "phone1",
      label: t("lblPhone1", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[12%]",
      type: "string",
      align: "left",
    },
    {
      name: "phone1Type",
      label: t("lblPhoneType", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[10%]",
      type: "string",
      align: "left",
      display: (_column: U2000TableColumn, value: unknown) => {
        if (!value) return "";
        if (value === U3120ContactPhoneType.OFFICE) return t("lblPhoneTypeOffice", { ns: NS });
        if (value === U3120ContactPhoneType.HOME) return t("lblPhoneTypeHome", { ns: NS });
        if (value === U3120ContactPhoneType.CELLPHONE) return t("lblPhoneTypeCellphone", { ns: NS });
        return String(value);
      },
    },
    {
      name: "phone1Extension",
      label: t("lblPhone1Extension", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[8%]",
      type: "string",
      align: "left",
      display: (_column: U2000TableColumn, value: unknown, row: U3121ProjectContact) => {
        if (row?.phone1Type !== U3120ContactPhoneType.OFFICE) return "";
        return String(value ?? "");
      },
    },
    {
      name: "notes",
      label: t("lblNotes", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[15%]",
      type: "string",
      align: "left",
    },
  ];
}

interface U3121ProjectContactBodyProps {
  formStateHandlers: U3121ProjectFormExtendedHandlers;
}

/**
 * Onglet Contact projet : affichage et édition des contacts du projet (projectContacts).
 * Toolbar Ajouter / Modifier / Supprimer ; synchronisation avec le projet parent.
 */
const U3121ProjectContactBody: React.FC<U3121ProjectContactBodyProps> = ({ formStateHandlers }) => {
  const { t, i18n } = useTranslation();
  const { hasRole } = useU2000AuthContext();
  const gridStateHandlers = useU2000GridStateHandlers<U3121ProjectContactWithSource>();
  const { search: onSearchContactType } = useU3121FindContactTypesHandlers();

  const projectData = formStateHandlers.getData?.() ?? null;
  const projectContacts = projectData?.projectContacts;

  const [formState, formStateDispatch] = React.useReducer(u2000FormState, u2000IntFormState);
  const businessHandlers: U2000FormSBusinessHandlers<U3121ProjectContact> = useMemo(
    () => ({
      newRecord: () => ({ ...emptyProjectContact }),
      validateForm: (data) =>
        u3121ProjectContactValidationErrors(data, {
          nameRequired: t("errContactNameRequired", { ns: NS }),
          typeRequired: t("errContactTypeRequired", { ns: NS }),
        }),
    }),
    [t],
  );
  const contactFormStateHandlers = useU2000FormStateHandlers<U3121ProjectContact>({
    formState,
    formStateDispatch,
    businessHandlers,
  });

  const handlerParams: U3121ProjectContactHandlerParam = {
    gridStateHandlers,
    t,
    formStateHandlers: contactFormStateHandlers,
    parentFormHandlers: formStateHandlers,
  };
  const handler = u3121ProjectContactHandler(handlerParams);

  useEffect(() => {
    const columns = getColumnsDef(t, i18n);
    gridStateHandlers.setColumns(columns);
    gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
    gridStateHandlers.setSelectedSortedFields(columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: "ASC" as const })));
    const list = Array.isArray(projectContacts) ? projectContacts : [];
    gridStateHandlers.setData(list);
    gridStateHandlers.setTotalRows(list.length);
    gridStateHandlers.setErrors([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gridStateHandlers / i18n objet exclus (boucle) ; projectContacts ref parent
  }, [t, i18n.language, projectContacts]);

  useEffect(() => {
    if (handler.dataChangedEvent === 0) return;
    const project = formStateHandlers.getData();
    if (!project) return;
    formStateHandlers.setData({
      ...project,
      projectContacts: gridStateHandlers.getData(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- aligné U3120 : seulement dataChangedEvent (pas formStateHandlers/gridStateHandlers)
  }, [handler.dataChangedEvent]);

  const isEditMode = formStateHandlers.getFormState?.() === U2000FormStateEnumState.EDIT;
  const disabledModify = !isEditMode || handler.gridStateHandlers.getSelectedRows().length !== 1;
  const disabledDelete = !isEditMode || handler.gridStateHandlers.getSelectedRows().length === 0;

  const actions = useMemo(
    () => [
      {
        key: "add",
        label: t("btnAdd", { ns: NS }),
        handleClick: handler.onAdd,
        hasRole: hasRole(U3121ROLES.EDIT),
        disabled: !isEditMode,
        icon: FaPlus,
        Component: U2000ActionBtn,
      },
      {
        key: "modify",
        label: t("btnModify", { ns: NS }),
        handleClick: handler.onModify,
        hasRole: hasRole(U3121ROLES.EDIT),
        disabled: disabledModify,
        icon: FaRegFile,
        Component: U2000ActionBtn,
      },
      {
        key: "delete",
        label: t("btnDelete", { ns: NS }),
        handleClick: handler.onDelete,
        hasRole: hasRole(U3121ROLES.EDIT),
        disabled: disabledDelete,
        icon: FaTrashAlt,
        Component: U2000ActionBtn,
      },
    ],
    [t, handler, isEditMode, disabledModify, disabledDelete, hasRole],
  );

  return (
    <>
      <U3121ProjectContactEditDlg u3121ProjectContactHandler={handler} t={t} NS={NS} onSearchContactType={onSearchContactType} />
      <U3121ProjectContactDeleteDlg u3121ProjectContactHandler={handler} t={t} NS={NS} />

      <div className="mt-1 mb-1 flex items-center gap-x-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} />
      </div>
      <U2000DetailTable
        gridStateHandlers={gridStateHandlers}
        rowClickFn={(row) => handler.onRowClick(row as U3121ProjectContact)}
        allowRowSelection={isEditMode}
        label={t("lblProjectContact", { count: gridStateHandlers.getTotalRows(), ns: NS })}
      />
    </>
  );
};

export default U3121ProjectContactBody;
